import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const TestimonialHighlights = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const testimonials = [
  {
    id: 1,
    name: 'Chioma Okafor',
    role: 'CEO, TechVentures Nigeria',
    company: 'Lagos, Nigeria',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a4c15496-1763297551142.png",
    avatarAlt: 'Professional headshot of Nigerian woman with braided hairstyle wearing elegant navy blue blazer and pearl necklace',
    rating: 5,
    quote: 'Edrian transformed our entire digital infrastructure. His technical expertise and strategic vision helped us scale from a local startup to serving clients across West Africa. The mobile app he developed increased our customer engagement by 300%.',
    project: 'E-commerce Platform Development',
    metrics: { revenue: '+250%', users: '50K+', rating: '4.8/5' }
  },
  {
    id: 2,
    name: 'Jean-Pierre Mbarga',
    role: 'Founder, InnovateHub',
    company: 'Douala, Cameroon',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1698c3078-1763296392738.png",
    avatarAlt: 'Professional portrait of Cameroonian businessman with short black hair wearing charcoal gray suit and burgundy tie',
    rating: 5,
    quote: 'Working with Edrian was a game-changer for our business. His mentorship helped me navigate the challenges of entrepreneurship, and his technical solutions gave us a competitive edge. He truly understands both technology and business.',
    project: 'Business Consulting & Web Development',
    metrics: { growth: '+180%', efficiency: '+65%', satisfaction: '98%' }
  },
  {
    id: 3,
    name: 'Thabo Nkosi',
    role: 'CTO, FinTech Solutions',
    company: 'Johannesburg, South Africa',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_16f144356-1763292286599.png",
    avatarAlt: 'Professional headshot of South African man with short fade haircut wearing modern black suit and crisp white shirt',
    rating: 5,
    quote: 'Edrian\'s creative direction and technical implementation exceeded all expectations. The brand identity he created resonated perfectly with our target market, and the web platform he built handles thousands of transactions daily without issues.',
    project: 'Brand Identity & Platform Development',
    metrics: { transactions: '10K/day', uptime: '99.9%', conversion: '+45%' }
  },
  {
    id: 4,
    name: 'Amara Eze',
    role: 'Junior Developer',
    company: 'Port Harcourt, Nigeria',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1bb2fd956-1763293568388.png",
    avatarAlt: 'Professional portrait of young Nigerian woman with natural afro hairstyle wearing teal blazer and gold hoop earrings',
    rating: 5,
    quote: 'As Edrian\'s mentee, I\'ve grown from a coding bootcamp graduate to a confident full-stack developer. His patient guidance, real-world project experience, and career advice have been invaluable. He genuinely cares about his mentees\' success.',
    project: 'Mentorship Program',
    metrics: { skills: '+12 Tech', projects: '8 Live', salary: '+200%' }
  }];


  useEffect(() => {
    const interval = setInterval(() => {
      setDirection(1);
      setActiveIndex((prev) => (prev + 1) % testimonials?.length);
    }, 8000);

    return () => clearInterval(interval);
  }, [testimonials?.length]);

  const handleNext = () => {
    setDirection(1);
    setActiveIndex((prev) => (prev + 1) % testimonials?.length);
  };

  const handlePrev = () => {
    setDirection(-1);
    setActiveIndex((prev) => (prev - 1 + testimonials?.length) % testimonials?.length);
  };

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: [0.6, 0.05, 0.01, 0.9]
      }
    },
    exit: (direction) => ({
      x: direction > 0 ? -1000 : 1000,
      opacity: 0,
      transition: {
        duration: 0.5,
        ease: [0.6, 0.05, 0.01, 0.9]
      }
    })
  };

  const currentTestimonial = testimonials?.[activeIndex];

  return (
    <section className="py-12 md:py-16 lg:py-24 px-4 md:px-6 lg:px-8 bg-gradient-to-b from-background to-card overflow-hidden">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-10 md:mb-14 lg:mb-20">

          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 rounded-full bg-accent/10 border border-accent/20 mb-4 md:mb-6"
            whileHover={{ scale: 1.05 }}>

            <Icon name="MessageSquare" size={16} color="var(--color-accent)" />
            <span className="text-xs md:text-sm lg:text-base font-medium text-accent">
              Client Success Stories
            </span>
          </motion.div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-5 lg:mb-6">
            <span className="bg-gradient-to-r from-accent via-primary to-secondary bg-clip-text text-transparent">
              What Clients Say
            </span>
          </h2>
          <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto">
            Real feedback from satisfied clients across Nigeria, Cameroon, and South Africa
          </p>
        </motion.div>

        {/* Testimonial Carousel */}
        <div className="relative">
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              key={activeIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              className="relative">

              <div className="bg-card rounded-2xl border border-border p-6 md:p-8 lg:p-10 shadow-2xl">
                <div className="flex flex-col lg:flex-row gap-6 md:gap-8 lg:gap-10">
                  {/* Client Info */}
                  <div className="flex-shrink-0">
                    <div className="flex flex-col items-center lg:items-start">
                      <motion.div
                        className="relative w-24 h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 rounded-full overflow-hidden mb-4 md:mb-5"
                        whileHover={{ scale: 1.05 }}>

                        <Image
                          src={currentTestimonial?.avatar}
                          alt={currentTestimonial?.avatarAlt}
                          className="w-full h-full object-cover" />

                        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20" />
                      </motion.div>

                      <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-foreground mb-1 text-center lg:text-left">
                        {currentTestimonial?.name}
                      </h3>
                      <p className="text-sm md:text-base text-primary font-medium mb-1 text-center lg:text-left">
                        {currentTestimonial?.role}
                      </p>
                      <p className="text-xs md:text-sm text-muted-foreground mb-3 md:mb-4 text-center lg:text-left">
                        {currentTestimonial?.company}
                      </p>

                      {/* Rating Stars */}
                      <div className="flex gap-1 mb-4 md:mb-5">
                        {[...Array(currentTestimonial?.rating)]?.map((_, i) =>
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: i * 0.1 }}>

                            <Icon name="Star" size={16} color="var(--color-accent)" className="fill-accent" />
                          </motion.div>
                        )}
                      </div>

                      {/* Project Badge */}
                      <div className="px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-primary/10 border border-primary/20">
                        <span className="text-xs md:text-sm text-primary font-medium">
                          {currentTestimonial?.project}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Testimonial Content */}
                  <div className="flex-1">
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.2 }}>

                      <Icon name="Quote" size={40} color="var(--color-primary)" className="mb-4 md:mb-5 opacity-50" />
                      <p className="text-base md:text-lg lg:text-xl text-foreground leading-relaxed mb-6 md:mb-8">
                        {currentTestimonial?.quote}
                      </p>

                      {/* Metrics */}
                      <div className="grid grid-cols-3 gap-4 md:gap-6">
                        {Object.entries(currentTestimonial?.metrics)?.map(([key, value], idx) =>
                        <motion.div
                          key={key}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 + idx * 0.1 }}
                          className="text-center p-3 md:p-4 rounded-xl bg-muted/50">

                            <div className="text-xl md:text-2xl lg:text-3xl font-bold text-primary mb-1">
                              {value}
                            </div>
                            <div className="text-xs md:text-sm text-muted-foreground capitalize">
                              {key}
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Controls */}
          <div className="flex items-center justify-center gap-4 mt-6 md:mt-8 lg:mt-10">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={handlePrev}
              className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-card border border-border flex items-center justify-center hover:border-primary transition-colors"
              aria-label="Previous testimonial">

              <Icon name="ChevronLeft" size={20} color="currentColor" />
            </motion.button>

            {/* Dots Indicator */}
            <div className="flex gap-2">
              {testimonials?.map((_, index) =>
              <motion.button
                key={index}
                onClick={() => {
                  setDirection(index > activeIndex ? 1 : -1);
                  setActiveIndex(index);
                }}
                className={`w-2 h-2 md:w-2.5 md:h-2.5 rounded-full transition-all ${
                index === activeIndex ?
                'bg-primary w-6 md:w-8' : 'bg-muted hover:bg-muted-foreground'}`
                }
                whileHover={{ scale: 1.2 }}
                aria-label={`Go to testimonial ${index + 1}`} />

              )}
            </div>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleNext}
              className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-card border border-border flex items-center justify-center hover:border-primary transition-colors"
              aria-label="Next testimonial">

              <Icon name="ChevronRight" size={20} color="currentColor" />
            </motion.button>
          </div>
        </div>

        {/* Trust Badges */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="flex flex-wrap items-center justify-center gap-6 md:gap-8 lg:gap-10 mt-10 md:mt-12 lg:mt-16">

          {[
          { icon: 'Award', label: '50+ Projects', color: 'primary' },
          { icon: 'Users', label: '40+ Clients', color: 'secondary' },
          { icon: 'Star', label: '4.9/5 Rating', color: 'accent' },
          { icon: 'Globe', label: '3 Countries', color: 'success' }]?.
          map((badge, index) =>
          <motion.div
            key={index}
            className="flex items-center gap-2 md:gap-3"
            whileHover={{ scale: 1.05 }}>

              <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full bg-${badge?.color}/10 flex items-center justify-center`}>
                <Icon name={badge?.icon} size={16} color={`var(--color-${badge?.color})`} />
              </div>
              <span className="text-sm md:text-base font-medium text-foreground">
                {badge?.label}
              </span>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>);





};

export default TestimonialHighlights;